import { AddRounded, NavigateNextRounded } from "@mui/icons-material";
import { Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Typography, Chip, IconButton, Stack, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { DateTime } from "luxon";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Employee } from "../../types/Employee";
import { Vacation } from "../../types/Vacation";
import { queryVacationByIdNumber } from "../../utils/queryVacations";
import { ChipIcon, ChipColor } from "../ChipUtils";
import { VacationRequestForm } from "../vacation/VacationRequestForm";
import { useSnackbar } from "notistack";
import { createVacationRequest } from "../../utils/createVacationRequest";
import { SecureComponent } from "../../security/SecureComponent";

export const EmployeeVacationRequests = (props: { employee: Employee }) => {
  const { employee } = props;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [vacations, setVacations] = useState<Vacation[]>([]);
  
  const [showVacationRequestForm, setShowVacationRequestForm] = useState(false);
  const [startDate, setStartDate] = useState(DateTime.now().toFormat('yyyyMMdd'));
  const [endDate, setEndDate] = useState(DateTime.now().toFormat('yyyyMMdd'));
  const [disableSaveButton, setDisableSaveButton] = useState(false);

  const refresh = () => {
    queryVacationByIdNumber(employee.idNumber).then((vacations) => {
      setVacations(vacations);
    });
  };
  
  const handleVacationRequestFormOnChange = (_e: Employee, s: string, e: string) => {
    setStartDate(s);
    setEndDate(e);
  };

  const handleShowVacationRequestForm = () => setShowVacationRequestForm(true);
  const handleCancelVacationRequestForm = () => {
    setStartDate(DateTime.now().toFormat('yyyyMMdd'));
    setEndDate(DateTime.now().toFormat('yyyyMMdd'));
    setShowVacationRequestForm(false);
  };

  const handleSaveVacationRequest = () => {
    if (window.confirm('Are you sure you want to save the vacation request?')) {
      setDisableSaveButton(true);
      createVacationRequest(employee, startDate, endDate).then(r => {
        if (r) {
          refresh();
          enqueueSnackbar('Vacation request has been created!', { variant: 'success' });
        }
        setStartDate(DateTime.now().toFormat('yyyyMMdd'));
        setEndDate(DateTime.now().toFormat('yyyyMMdd'));
        setShowVacationRequestForm(false);
        setDisableSaveButton(false);
      });
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <Stack>
      <SecureComponent permissions={['create.vacations']}>
        <Stack direction='row-reverse' mb={1}>
          <Button startIcon={<AddRounded />} onClick={handleShowVacationRequestForm}>Create new request</Button>
        </Stack>
      </SecureComponent>
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={76} align="right">Count</TableCell>
                <TableCell width={120}>Id Number</TableCell>
                <TableCell>Name</TableCell>
                <TableCell width={220}>Date Requested</TableCell>
                <TableCell width={150}>Request Status</TableCell>
                <TableCell width={40}>Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vacations.length === 0 && (
                <TableRow
                  sx={{
                    cursor: 'pointer',
                    '&:last-child td, &:last-child th': { border: 0 } 
                  }}>
                  <TableCell colSpan={6}><Typography variant="caption">No vacation requests to show...</Typography></TableCell>
                </TableRow>
              )}
              {vacations.map((v, i) => {
                return (
                  <TableRow 
                    hover
                    key={v.id}
                    sx={{
                      cursor: 'pointer',
                      '&:last-child td, &:last-child th': { border: 0 } 
                    }}
                    // onClick={() => setSelected(v)}
                  >
                    <TableCell align="right">{i + 1}</TableCell>
                    <TableCell>{v.idNumber}</TableCell>
                    <TableCell>{v.name}</TableCell>
                    <TableCell>{v.dateRequested ? DateTime.fromISO(v.dateRequested).toLocaleString(DateTime.DATETIME_MED) : ''}</TableCell>
                    <TableCell>
                      <Chip icon={ChipIcon(v.requestStatus!)} label={v.requestStatus} color={ChipColor(v.requestStatus!)} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/services/vacationRequests/${v.id}`, {
                            state: {
                              vacation: v,
                            }
                          })
                        }}
                      >
                        <NavigateNextRounded />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <SecureComponent permissions={['create.vacations']}>
        <Dialog open={showVacationRequestForm} onClose={() => setShowVacationRequestForm(false)} fullWidth>
          <DialogTitle>Create Vacation Request</DialogTitle>
          <DialogContent dividers>
            <VacationRequestForm employee={employee} onChanged={handleVacationRequestFormOnChange} />
          </DialogContent>
          <DialogActions>
            <Button color="error" onClick={handleCancelVacationRequestForm}>CANCEL</Button>
            <Button color="primary" onClick={handleSaveVacationRequest} disabled={disableSaveButton}>SAVE</Button>
          </DialogActions>
        </Dialog>
      </SecureComponent>
    </Stack>
  )
};